/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ "X-Admin-Password": sessionStorage.getItem("adminPassword") || "" });
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");
const ACTION = { accept_all: ["Tout accepté", "bg-green-600"], reject_all: ["Tout refusé", "bg-gray-600"], custom: ["Personnalisé", "bg-[#2196F3]"] };

export const ConsentSection = () => {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/consent-logs?limit=300`, { headers: adminHeaders() });
      setData(res.data);
    } catch { toast.error("Chargement impossible"); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/consent-logs/export.csv`, { headers: adminHeaders(), responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = `MGB-School_registre-consentements_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
    } catch { toast.error("Export impossible"); }
  };

  if (!data) return <p className="text-sm text-gray-500">Chargement…</p>;
  const pct = (n) => (data.total ? Math.round((100 * n) / data.total) : 0);

  return (
    <div className="space-y-6" data-testid="consent-section">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-[#003087] flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Registre des consentements cookies (preuve CNIL)</CardTitle>
              <CardDescription>Chaque clic sur le bandeau est journalisé : identifiant anonyme, catégories, horodatage, IP hachée, navigateur. Aucune donnée directement identifiante.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
              <Button onClick={exportCsv} className="bg-[#003087] hover:bg-[#002366] text-white" data-testid="consent-export-csv"><Download className="h-4 w-4 mr-1" /> Exporter CSV</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[["Total", data.total, "text-[#003087]"], ["Tout accepté", data.by_action.accept_all, "text-green-700"], ["Tout refusé", data.by_action.reject_all, "text-gray-700"], ["Personnalisé", data.by_action.custom, "text-[#2196F3]"]].map(([l, n, c]) => (
              <div key={l} className="p-4 rounded-lg border border-gray-200 bg-gray-50" data-testid={`consent-stat-${l}`}>
                <p className="text-xs uppercase tracking-wider text-gray-500">{l}</p>
                <p className={`text-2xl font-heading font-bold ${c}`}>{n}{l !== "Total" && <span className="text-sm font-normal text-gray-500 ml-1">({pct(n)} %)</span>}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="consent-table">
              <thead><tr className="text-left text-xs uppercase text-gray-500 border-b">
                <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Action</th><th className="py-2 pr-3">Statistiques</th><th className="py-2 pr-3">Publicité</th><th className="py-2 pr-3">Enreg. session</th><th className="py-2 pr-3">Page</th><th className="py-2 pr-3">Identifiant</th>
              </tr></thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmt(r.at)}</td>
                    <td className="py-2 pr-3"><Badge className={`${ACTION[r.action]?.[1] || "bg-gray-400"} text-white text-[10px]`}>{ACTION[r.action]?.[0] || r.action}</Badge></td>
                    <td className="py-2 pr-3">{r.prefs?.analytics ? "oui" : "non"}</td>
                    <td className="py-2 pr-3">{r.prefs?.marketing ? "oui" : "non"}</td>
                    <td className="py-2 pr-3">{r.prefs?.recording ? "oui" : "non"}</td>
                    <td className="py-2 pr-3 text-xs text-gray-500">{r.page}</td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-gray-400">{(r.consent_id || "").slice(0, 8)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
